import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { Communication, ChannelType, CommunicationRecipientActivity, ChannelUIds } from 'src/app/modules/management/store/communication-center';
import { TemplateControlService } from 'src/app/modules/core/components/template-builder/controls/template-control.service';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import * as XLSX from 'xlsx';
import { convert } from 'html-to-text';
import * as fromSharing from 'src/app/modules/sharing/store';
import { MassNotification } from '@app/modules/communications/store/communications';
import { CommunicationsApiService } from '@app/modules/communications/store/communications/communications-api.service';
import { ChannelSelectionService } from '@app/modules/communications/services/channel-selection-service';
@Component({
    selector: 'topo-communication-preview',
    templateUrl: './communication-preview.component.html',
    styleUrls: ['./communication-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CommunicationPreviewComponent implements OnInit, OnDestroy {
  isActive: boolean;
  destroy$: Subject<void> = new Subject<void>();
  recipientsInput$ = new Subject<string>();
  toSelectedRecipients: any[] = [];
  ccSelectedRecipients: any[] = [];
  channelType = ChannelType;
  selectedChannelName: string;
  selectedChannelUId: string;
  recipientsLoading: boolean;
  isSlackTemplate: boolean;
  isEmailTemplate: boolean;

  title: string;
  subject: string;
  selectedTabIndex = 0;
  communication: Communication;
  configuredChannelUId: string;
  logNotification: MassNotification;
  body: string;
  recipients: CommunicationRecipientActivity[];
  isPlainText: boolean = false;

  constructor(
    private store: Store<any>,
    public templateControlService: TemplateControlService,
    private commApiService: CommunicationsApiService,
    private translate: TranslateService,
    private channelSelectionService: ChannelSelectionService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.store
      .select(fromSharing.selectShowHistoricCommunication)
      .pipe(takeUntil(this.destroy$))
      .subscribe(showDialog => {
        this.isActive = showDialog;
        this.changeDetector.markForCheck();
      });
    this.store
      .select(fromSharing.selectHistoricCommunication)
      .pipe(
        takeUntil(this.destroy$),
        filter(c => !!c?.communication && !!c?.channelUId &&  !!c?.channelName)
      )
      .subscribe(async c => {
        this.communication = c.communication;
        this.selectedChannelUId = c.channelUId;
        this.selectedChannelName = c.channelName;
        this.isSlackTemplate = this.selectedChannelUId === ChannelUIds.SlackChannelUId;
        this.title = this.selectedChannelName;
        this.subject = this.communication?.customSubject;
        this.body =
          this.communication.renderedContent ?? this.communication?.customHtmlContent ?? this.communication?.customTextContent ?? '';
        // Display whitespace and newlines for non-html message content
        this.isPlainText = !this.body.includes('<table');
        this.recipients = c.recipients;

        this.changeDetector.markForCheck();
      });
  }

  exportToExcel() {
    const detailsWs = this.generateCommunicationDetailsExcelSheet();

    const recipientsWs = this.generateRecipientsExcelSheet();

    /* generate workbook and add the worksheets */
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, detailsWs, 'Communication Details');
    XLSX.utils.book_append_sheet(wb, recipientsWs, 'Recipients');

    const now = new Date();
    const dateString =
      now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate() + '_' + now.getHours() + now.getMinutes() + now.getSeconds();
    /* save to file */
    XLSX.writeFile(wb, `TopoCommunicationExport_${dateString}.xlsx`);
  }

  generateCommunicationDetailsExcelSheet(): XLSX.WorkSheet {
    const rows = [];

    // add the header row
    const headerRow = [];
    const columnSettings = [];
    const columns = ['Communication UId', 'Subject', 'Body', 'Channel UId', 'Channel Name', 'Channel Type', 'Last Updated Time'];

    for (const column of columns) {
      headerRow.push(column);

      columnSettings.push({
        wpx: 175
      });
    }
    rows.push(headerRow);

    let exportBody = this.body;

    if (!this.isPlainText) {
      // HTML to plain text
      exportBody = convert(exportBody, { wordwrap: 130 });

      // Remove encoded images
      const regex = /\[data:image.*\]/gi;
      exportBody = exportBody.replace(regex, '[image attachment]');
    }

    if (exportBody.length > 32767) {
      exportBody = exportBody.substring(0, 32700) + '\n-- [Truncated Message] --';
    }

    rows.push([
      this.communication.uId,
      this.subject,
      exportBody,
      this.communication.configuredChannelUId,
      this.selectedChannelName,
      this.channelSelectionService.getChannelName(this.selectedChannelUId),
      this.communication.updatedOn
    ]);

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = columnSettings;
    return ws;
  }

  generateRecipientsExcelSheet(): XLSX.WorkSheet {
    const rows = [];

    // add the header row
    const headerRow = [];
    const columnSettings = [];
    const columns = [
      'Recipient UId',
      'Recipient',
      'Name',
      'Distribution List UId',
      'Distribution List Name',
      'Recipient Type',
      'Status',
      'Sent Time'
    ];

    for (const column of columns) {
      headerRow.push(column);

      columnSettings.push({
        wpx: 175
      });
    }
    rows.push(headerRow);

    // add recipient rows
    for (const recipient of this.recipients) {
      const rowData = [
        recipient.uId,
        recipient.recipient,
        recipient.name,
        recipient.distributionListUId,
        recipient.distributionListName,
        this.translate.instant('MAP.RECIPIENT-TYPE-' + recipient.recipientType),
        this.translate.instant('MAP.COMMUNICATION-RECIPIENT-STATUS-' + recipient.status),
        recipient.sentTime
      ];

      rows.push(rowData);
    }

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = columnSettings;
    return ws;
  }

  cancelClick(): void {
    this.store.dispatch(fromSharing.hideCommunicationHistoryDialog());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  contentScrolled(): void {
    this.templateControlService.hideSettingsControlIfNotEdit();
  }

  onClose() {
    this.cancelClick();
  }

  logClick(): void {
    this.commApiService.getMassNotification(this.communication?.massNotificationUId).subscribe(result => {
      this.configuredChannelUId = this.communication?.configuredChannelUId;
      this.logNotification = result;
      this.changeDetector.markForCheck();
    })
  }

  onLogClosed(): void {
    this.logNotification = null;
    this.changeDetector.markForCheck();
  }
}
